import React, { useState, useEffect } from "react";
import InputField from "../../components/InputField";
import Switch from "../../components/Switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import ToolTip from "../ToolTip";
import Dropdown from "../Dropdown";
import ListValueField from "../ListValueField";
import SuggestInputField from "../SuggestInputField";
import CustomQuestionFields from "./CustomQuestionFields";
import AccountTypeDropdown from "./AccountTypeDropdown";
import GroupMembershipPicker from "./GroupMembershipPicker";
import AddOnLicensePicker from "./AddOnLicensePicker";

/**
 * Explains why a field is shown but cannot be changed (PBI #317). Name and email are
 * protected because changing them breaks synchronisation, worst of all against
 * on-premises AD, so the need is handled as a support case instead. The wording and the
 * support address come from the server, not from this file.
 */
const ProtectedFieldNote = ({ text }) =>
  text ? <p className="text-sm text-gray-600 mt-1">{text}</p> : null;

const UserForm = ({
  user,
  setUser,
  setHasChanges,
  billingGroups,
  roles,
  onBillingGroupChange,
  protectedFieldHelpText,
  // PBI #316: manager choices as "Name (upn)" labels, built by the page so two people
  // with the same name stay distinguishable.
  managerOptions = [],
  // PBI #380: the customer's account types, shown read-only — the account type cannot be
  // changed from the edit.
  accountTypes = [],
  // PBI #299: which account type the user has, by public id — the name is not unique
  // within an agreement, so the description must be resolved on the id.
  accountTypePublicId = "",
  onManagerChange,
  // PBI #316: same list values and policy as the onboarding form (PBI #292).
  scoFormSettings = {},
  jobTitleOptions = [],
  officeOptions = [],
  countryOptions = [],
  companyOptions = [],
  departmentOptions = [],
  // PBI #320: the customer's own questions; answers only travel in the
  // verification mail to Diflex.
  customQuestions = [],
  customAnswers = {},
  onCustomAnswerChange = () => {},
  // PBI #372: the customer's Entra groups with the sync status, and the user's ticked
  // groups. No groupOptions (the fetch failed) means no picker.
  groupOptions = null,
  selectedGroupIds = [],
  onGroupsChange = () => {},
  // PBI #373: add-on licences from the customer's own portfolio, base licence excluded.
  addOnLicenses = [],
  addOnLicensePublicIds = [],
  onAddOnLicenseChange = () => {},
}) => {
  const selectedBillingGroup = user?.billingGroupName || "";
  const [selectedRole, setSelectedRole] = useState("");

  useEffect(() => {
    if (user?.roleName) {
      setSelectedRole(user?.roleName || "");
    }
  }, [user, roles]);

  return (
    <>
      {/* Two-column layout */}
      <div className="grid grid-cols-2 gap-6 w-full">
        {/* Column 1 */}
        <div className="flex flex-col gap-4">
          <Dropdown
            label="Billing group"
            value={selectedBillingGroup}
            options={billingGroups.map((group) => group.name)}
            onSelect={(option) => {
              onBillingGroupChange(option);
              setHasChanges(true);
            }}
            disabled={false}
          />

          {/* PBI #316: editable by the customer. Saved to the User row and synced to
              Entra through an UpdateUserDetails change request. The list values and the
              custom-value policy are the onboarding form's (PBI #292). */}
          <ListValueField
            label="Country"
            placeholder="Country"
            value={user?.country || ""}
            options={countryOptions}
            allowCustom={scoFormSettings?.allowCustomCountry}
            onChange={(value) => {
              setUser((s) => ({ ...s, country: value }));
              setHasChanges(true);
            }}
          />

          <ListValueField
            label="Title"
            placeholder="Title"
            value={user?.jobTitle || ""}
            options={jobTitleOptions}
            allowCustom={scoFormSettings?.allowCustomJobTitle}
            onChange={(value) => {
              setUser((s) => ({ ...s, jobTitle: value }));
              setHasChanges(true);
            }}
          />

          {/* #357: Office, Company and Department offer the customer's values and take a
              new one, as on the onboarding form. */}
          <SuggestInputField
            label="Company"
            placeholder="Company"
            value={user?.company || ""}
            options={companyOptions}
            onChange={(value) => {
              setUser((s) => ({ ...s, company: value }));
              setHasChanges(true);
            }}
          />

          <SuggestInputField
            label="Department"
            placeholder="Department"
            value={user?.department || ""}
            options={departmentOptions}
            onChange={(value) => {
              setUser((s) => ({ ...s, department: value }));
              setHasChanges(true);
            }}
          />

          <InputField
            label="Licenses"
            placeholder="Licenses"
            value={
              user?.licenses
                ? Object.values(user?.licenses).join(", ")
                : "No licenses"
            }
            onChange={(e) => {
              setUser((s) => ({ ...s, licenses: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={true}
          />

          <AddOnLicensePicker
            options={addOnLicenses}
            selected={addOnLicensePublicIds}
            onChange={onAddOnLicenseChange}
          />

          <div className="w-full">
            <InputField
              label="Name"
              placeholder="Name"
              value={user?.displayName || ""}
              onChange={(e) => {
                setUser((s) => ({ ...s, displayName: e.target?.value }));
                setHasChanges(true);
              }}
              readOnly
              className="w-full"
              disabled={true}
            />
            <ProtectedFieldNote text={protectedFieldHelpText} />
          </div>

          <div className="w-full">
            <InputField
              label="Email"
              placeholder="Email"
              value={user?.email || ""}
              onChange={(e) => {
                setUser((s) => ({ ...s, email: e.target?.value }));
                setHasChanges(true);
              }}
              readOnly
              className="w-full"
              disabled={true}
            />
            <ProtectedFieldNote text={protectedFieldHelpText} />
          </div>

          <InputField
            label="Last login"
            placeholder="Last signed in date"
            value={user?.lastSignInDateTime || ""}
            onChange={(e) => {
              setUser((s) => ({ ...s, lastSignedInDate: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={true}
          />

          {/* PBI #316: the manager is chosen among the customer's current users; the
              write to Entra is a directory reference, so free text cannot work here. */}
          <Dropdown
            label="Manager"
            value={user?.managerDisplayName || ""}
            options={managerOptions}
            onSelect={(option) => {
              onManagerChange?.(option);
              setHasChanges(true);
            }}
            searchable={true}
          />

          <div className="flex flex-col w-1/2">
            <label className="text-sm font-bold pb-2">On-premises sync</label>
            <div className="flex items-center space-x-2">
              <Switch
                checked={user?.accountEnabled}
                onChange={(checked) => {
                  setUser((s) => ({ ...s, accountEnabled: checked }));
                  setHasChanges(true);
                }}
                disabled={true}
              />
              <p className="text-sm">Sync</p>
            </div>
          </div>
        </div>

        {/* Column 2 */}
        <div className="flex flex-col gap-4">
        <Dropdown
            label="Role"
            value={user?.roles?.[0] || ""}
            options={roles}
            onSelect={(option) => {
              setUser((prev) => ({
                ...prev,
                roles: [option],
              }));
              setHasChanges(true);
            }}
            disabled={false}
          />

          {groupOptions && (
            <GroupMembershipPicker
              status={groupOptions.status}
              asOfUtc={groupOptions.asOfUtc}
              options={groupOptions.groups}
              selected={selectedGroupIds}
              onChange={(selectedIds) => {
                onGroupsChange(selectedIds);
                setHasChanges(true);
              }}
            />
          )}

          <div className="flex flex-col">
            <label className="text-sm font-bold flex space-x-2">
              <span>User principal name</span>
              <ToolTip text={"This is the email address of the user"}>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-gray-400 text-md"
                />
              </ToolTip>
            </label>

            <InputField
              placeholder="User principal name"
              value={user?.userPrincipalName || ""}
              onChange={(e) => {
                setUser((s) => ({ ...s, userPrincipalName: e.target?.value }));
                setHasChanges(true);
              }}
              readOnly
              className="w-full"
              disabled={true}
            />
          </div>

          <InputField
            label="Updated by"
            placeholder="Updated by"
            value={user?.updatedBy || ""}
            onChange={(e) => {
              setUser((s) => ({ ...s, updatedBy: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={true}
          />

          <InputField
            label="Last update"
            placeholder="Last update"
            value={user?.updatedDate || ""}
            onChange={(e) => {
              setUser((s) => ({ ...s, updatedDate: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={true}
          />

          {/* PBI #380: locked like Name and Email — Diflex changes the account type. */}
          <div className="w-full">
            <AccountTypeDropdown
              label="Account type"
              accountTypes={accountTypes}
              selectedName={user?.accountTypeName}
              selectedPublicId={accountTypePublicId}
              disabled={true}
            />
            <ProtectedFieldNote text={protectedFieldHelpText} />
          </div>

          <InputField
            label="Mobile phone"
            placeholder="Mobile phone"
            value={user?.mobilePhone || ""}
            onChange={(e) => {
              setUser((s) => ({ ...s, mobilePhone: e.target?.value }));
              setHasChanges(true);
            }}
            className="w-full"
          />

          <InputField
            label="Phone"
            placeholder="Phone"
            value={user?.phone || ""}
            onChange={(e) => {
              setUser((s) => ({ ...s, phone: e.target?.value }));
              setHasChanges(true);
            }}
            className="w-full"
          />

          <SuggestInputField
            label="Office location"
            placeholder="Office location"
            value={user?.officeLocation || ""}
            options={officeOptions}
            onChange={(value) => {
              setUser((s) => ({ ...s, officeLocation: value }));
              setHasChanges(true);
            }}
          />

          {/* PBI #320: the customer's own questions, configured per customer by Diflex. */}
          <CustomQuestionFields
            questions={customQuestions}
            answers={customAnswers}
            onAnswerChange={onCustomAnswerChange}
          />
        </div>
      </div>
    </>
  );
};

export default UserForm;
